import { Box, Button, Grid, LinearProgress, Typography, styled, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import useBucketsByUserId from '../../../hooks/useBucketsByUserId'
import { Bucket, UserProfile } from '../../../../../graphql/generated'
import BucketProgress from './BucketProgress'

const BucketsGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '414px'
}))

const BucketsGridItem = styled(Grid)(() => ({
  maxWidth: '120px'
}))

const ExceededAlert = styled(Box)(({ theme }) => ({
  maxWidth: '414px',
  marginBottom: theme.spacing(3)
}))

interface FreemiumProps {
  userProfile: UserProfile
  openChangePlan: () => void
}

export default function Freemium({ userProfile, openChangePlan }: FreemiumProps) {
  const { items, loading } = useBucketsByUserId()
  const [ownedBuckets, setOwnedBuckets] = useState<Bucket[]>([])
  const theme = useTheme()

  useEffect(() => {
    setOwnedBuckets(items.filter((currentBucket) => currentBucket.ownerId === userProfile?.id))
  }, [userProfile, items])

  const handleDeleteClick = () => {
    window.location.href = 'https://my.photobucket.com/photobucket-account-deletion-step1'
  }

  const exceededLimit = ownedBuckets.some(
    (bucket) => bucket.counters?.totalMedia && bucket.counters.totalMedia >= (userProfile?.plan?.bucket_media_limit ?? 50)
  )

  const colors = [theme.palette.groupsyPink['600'], theme.palette.customYellow['600'], theme.palette.customViolet['600']]
  const bgColors = [theme.palette.groupsyPink['50'], theme.palette.customYellow['100'], theme.palette.customViolet['50']]

  return (
    <Box>
      <Typography variant="pb.title.large" marginBottom={4}>
        You&apos;re on a free plan...
      </Typography>
      <BucketsGrid container spacing={4}>
        {loading && <LinearProgress />}
        {ownedBuckets.map((bucket, i) => (
          <BucketsGridItem item key={bucket.id}>
            <BucketProgress bucket={bucket} userProfile={userProfile} color={colors[i]} bgColor={bgColors[i]} />
          </BucketsGridItem>
        ))}
      </BucketsGrid>
      {exceededLimit && (
        <ExceededAlert>
          <Typography variant="pb.label.small" marginBottom={2}>
            You&apos;ve exceeded your storage for the free plan. Upgrade for unlimited buckets and images.
          </Typography>
        </ExceededAlert>
      )}
      <Box marginBottom={4}>
        <Typography variant="pb.title.large">Freemium Plan</Typography>
        <Grid container style={{ margin: '20px 0', fontSize: '14px' }}>
          <Grid item xs={12} md={4} lg={3}>
            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <li>3 Buckets</li>
              <li>50 Videos & Photos per Bucket</li>
              <li>Search & Organizational Tools</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <li>Image Editor</li>
              <li>WhatsApp & Facebook Transfer Integrations</li>
              <li>Autobackup</li>
            </ul>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
        <Button color="error" variant="outlined" sx={{ width: 'fit-content' }} onClick={handleDeleteClick}>
          Delete Account
        </Button>
        <Button
          data-testid="upgrade-or-change-plan"
          onClick={openChangePlan}
          variant="contained"
          sx={{ width: 'fit-content' }}
          data-pendo="upgrade-membership--step-01"
        >
          Upgrade Plan
        </Button>
      </Box>
    </Box>
  )
}
